import api from './api';
import { Organizer, OrganizerKind, PublicOrganizer, CreateOrganizerRequest, UpdateOrganizerRequest, OrganizerEditResponse, ProposeOrganizerEditRequest, ReviewOrganizerEditRequest, PageResponse, OrganizerDuplicateGroup, MergeOrganizersRequest, DismissOrganizerDuplicatesRequest } from '@radevents/shared';

const organizerService = {
  getAll: async (search?: string): Promise<Organizer[]> => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    const response = await api.get('/organizers/admin', { params });
    return response.data;
  },

  /** Public list (id + name, no contact data) — usable by anonymous visitors. */
  listPublic: async (search?: string): Promise<Organizer[]> => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    const response = await api.get('/organizers', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Organizer> => {
    const response = await api.get(`/organizers/${id}/full`);
    return response.data;
  },

  /** Public profile (incl. social links, no contact data) — usable by anonymous visitors. */
  getPublicById: async (id: number): Promise<PublicOrganizer> => {
    const response = await api.get(`/organizers/${id}`);
    return response.data;
  },

  create: async (data: CreateOrganizerRequest): Promise<Organizer> => {
    const response = await api.post('/organizers', data);
    return response.data;
  },

  update: async (id: number, data: UpdateOrganizerRequest): Promise<Organizer> => {
    const response = await api.put(`/organizers/${id}`, data);
    return response.data;
  },

  /**
   * Nur die Einordnung (Verein / kommerziell) setzen.
   *
   * Eigener Endpunkt statt `update`: der nimmt den ganzen Datensatz, und `logoUrl`/`photoUrl`
   * sind in der Liste bei hochgeladenen Bildern berechnete Abrufadressen — zurueckgeschickt
   * landeten sie in den Spalten.
   */
  setKind: async (id: number, orgKind: OrganizerKind): Promise<Organizer> => {
    const response = await api.put(`/organizers/${id}/kind`, { orgKind });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/organizers/${id}`);
  },

  enrichAddresses: async (): Promise<{ enriched: number; notFound: number; errors: number }> => {
    const response = await api.post('/organizers/enrich-addresses');
    return response.data;
  },

  importCsv: async (file: File): Promise<{ added: number; skipped: number; errors: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/organizers/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  proposeNew: async (data: ProposeOrganizerEditRequest): Promise<OrganizerEditResponse> => {
    const response = await api.post('/organizer-edits/propose', data);
    return response.data;
  },

  proposeEdit: async (organizerId: number, data: ProposeOrganizerEditRequest): Promise<OrganizerEditResponse> => {
    const response = await api.post(`/organizer-edits/propose/${organizerId}`, data);
    return response.data;
  },

  getPendingEdits: async (page = 0, size = 20): Promise<PageResponse<OrganizerEditResponse>> => {
    const response = await api.get('/organizer-edits/pending', { params: { page, size } });
    return response.data;
  },

  reviewEdit: async (id: number, data: ReviewOrganizerEditRequest): Promise<OrganizerEditResponse> => {
    const response = await api.post(`/organizer-edits/${id}/review`, data);
    return response.data;
  },

  approveAllEdits: async (): Promise<{ approved: number }> => {
    const response = await api.post('/organizer-edits/approve-all');
    return response.data;
  },

  getDuplicates: async (): Promise<OrganizerDuplicateGroup[]> => {
    const response = await api.get('/organizers/duplicates');
    return response.data;
  },

  mergeOrganizers: async (data: MergeOrganizersRequest): Promise<void> => {
    await api.post('/organizers/merge', data);
  },

  dismissDuplicates: async (data: DismissOrganizerDuplicatesRequest): Promise<void> => {
    await api.post('/organizers/duplicates/dismiss', data);
  },
};

export default organizerService;
